import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle, FormControlLabel,
  Table,
  TableBody, TableCell,
  TableContainer, TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';

import CogNeedSwitch from '../../../../../../../../../../../Common/CogNeedSwitch';
import {
  GetSessionEvaluationSheetsResultsQuery, SessionSummaryFieldsFragment, useGetSessionEvaluationSheetsResultsQuery, useListAdvancedAnalysisItemsQuery, useListComplianceOperationItemsQuery, useListOrganizationTopicsQuery,
} from '../../../../../../../../../../../generated/core-api/graphql-react-query';
import useRatioFormat from '../../../../../../../../../../../Hooks/useRatioFormat';
import useEvaluationSheetCsvRows from '../Hooks/useEvaluationSheetCsvRows';
import useListRelatedSessions from '../Hooks/useListRelatedSessions';
import useModuleGroupedEvaluationSheetResultItems from '../Hooks/useModuleGroupedEvaluationSheetResultItems';
import downloadCsv from '../utils/downloadCsv';
import EvaluationSheetUserTableRow from './EvaluationSheetUserTableRow/EvaluationSheetUserTableRow';
import StyledDialog from './styles';

interface SessionEvaluationSheetDetailsDialogProps {
  sheetAssignmentId: string | null;
  onClose: () => void;
  session: SessionSummaryFieldsFragment;
}

export default function SessionEvaluationSheetDetailsDialog(props: SessionEvaluationSheetDetailsDialogProps) {
  const { sheetAssignmentId, onClose, session } = props;

  const { t } = useTranslation('session');
  const { t: tCommon } = useTranslation('common');

  const totalPercentFormat = useRatioFormat('percentAndRatio');
  const relatedExternalCallGroupSessionsData = useListRelatedSessions(session, 'externalCallGroupId');
  const displayGroupedSessionsResults = session.externalCallGroupId && (relatedExternalCallGroupSessionsData?.sessions.length ?? 0) > 0;
  const [onlySessionState, setOnlySessionState] = React.useState(!session.externalCallGroupId);
  const [storeData, setStoreData] = React.useState<GetSessionEvaluationSheetsResultsQuery | null | undefined>(null);
  const topicsQuery = useListOrganizationTopicsQuery();
  const advancedAnalysisQuery = useListAdvancedAnalysisItemsQuery();
  const complianceOperationsQuery = useListComplianceOperationItemsQuery();

  const topicMap = React.useMemo(
    () => new Map((topicsQuery.data?.topics.items ?? []).map((topic) => [topic.textLower, { text: topic.text, color: topic.color }])),
    [topicsQuery.data],
  );

  const advancedAnalysisMap = React.useMemo(
    () => new Map(
      (advancedAnalysisQuery.data?.advancedAnalysisItem.list ?? []).map((item) => [item.id, { name: item.name, type: item.type }]),
    ),
    [advancedAnalysisQuery.data],
  );

  const complianceOperationMap = React.useMemo(
    () => new Map(
      (complianceOperationsQuery.data?.complianceOperationItem.list ?? []).map((item) => [item.id, { title: item.title, operator: item.operator }]),
    ),
    [complianceOperationsQuery.data],
  );

  const sessionEvaluationResultsData = useGetSessionEvaluationSheetsResultsQuery({
    input: { id: session.id },
    inputOptions: { onlySession: onlySessionState },
  }, {
    onSuccess: (data) => {
      setStoreData(data);
    },
  });
  const evaluationSheetResult = React.useMemo(() => {
    const data = storeData?.getSession?.evaluationSheetResults ?? [];
    const currentSheet = data.find((res) => res.id === sheetAssignmentId);
    return currentSheet;
  }, [storeData, sheetAssignmentId]);

  const { groupedByModule, hasAnyModule } = useModuleGroupedEvaluationSheetResultItems(evaluationSheetResult?.items ?? []);
  const { rows: csvRows } = useEvaluationSheetCsvRows(evaluationSheetResult);

  React.useEffect(() => {
    if (sessionEvaluationResultsData.data) {
      setStoreData(sessionEvaluationResultsData.data);
    }
  }, [sessionEvaluationResultsData.data]);

  return (
    <StyledDialog open={!!sheetAssignmentId} onClose={() => { onClose(); setStoreData(null); }} fullWidth maxWidth="md">
      <DialogTitle>
        {evaluationSheetResult?.evaluationSheet?.name}
        <Button
          variant="contained"
          title={tCommon('export-csv')}
          onClick={() => downloadCsv(
            `EvaluationSheet-${evaluationSheetResult?.evaluationSheet?.name}.csv`,
            csvRows,
          )}
          disabled={!evaluationSheetResult || csvRows.length === 0}
        >
          {tCommon('export-csv')}
        </Button>
      </DialogTitle>
      {evaluationSheetResult && (
      <DialogContent>
        {displayGroupedSessionsResults && (
          <Box>
            <Typography variant="body1">
              {t('details.evaluation-sheets.related-sessions', { count: relatedExternalCallGroupSessionsData?.sessions.length })}
            </Typography>
            <FormControlLabel
              labelPlacement="end"
              control={(
                <CogNeedSwitch
                  size="small"
                  checked={!!onlySessionState}
                  onChange={(_e, checked) => {
                    if (checked) {
                      setOnlySessionState(true);
                    } else {
                      setOnlySessionState(false);
                    }
                  }}
                />
              )}
              label={t('details.evaluation-sheets.only-session-cta')}
            />
          </Box>
        )}
        <TableContainer className={clsx({ isLoading: sessionEvaluationResultsData.isLoading })}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {hasAnyModule && <TableCell>{t('details.evaluation-sheets.columns.module')}</TableCell>}
                <TableCell>{t('details.evaluation-sheets.columns.item')}</TableCell>
                <TableCell>{t('details.evaluation-sheets.columns.score')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groupedByModule.flatMap(({
                module, items, totalNum, totalDenom,
              }) => (
                [
                  ...items.map((item, index) => (
                    <EvaluationSheetUserTableRow
                      groupLength={items.length + (module ? 1 : 0)}
                      key={item.id}
                      item={item}
                      index={index}
                      moduleName={module?.name}
                      topicMap={topicMap}
                      advancedAnalysisMap={advancedAnalysisMap}
                      complianceOperationMap={complianceOperationMap}
                    />
                  )),
                  ...!module ? [] : [
                    <TableRow key={`module-${module.id}-total`} className="total-row module">
                      <TableCell colSpan={3} align="right">
                        {totalPercentFormat.format({ numerator: totalNum, denominator: totalDenom })}
                      </TableCell>
                    </TableRow>,
                  ],
                ]
              ))}
              <TableRow key="evaluation-sheet-total" className="total-row overall">
                <TableCell colSpan={4} align="right">
                  {totalPercentFormat.format({
                    numerator: evaluationSheetResult.num ?? 0,
                    denominator: evaluationSheetResult.denom ?? 0,
                  })}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      )}
      <DialogActions>
        <Button variant="outlined" onClick={() => { onClose(); setStoreData(null); }}>{tCommon('close')}</Button>
      </DialogActions>
    </StyledDialog>
  );
}
